import React, { forwardRef } from 'react';

/**
 * PhotoFrame — the exportable frame component
 * Renders the themed frame + photo grid.
 * Wrapped in forwardRef so parent can capture with html-to-image.
 *
 * Props:
 *   layout  — from LAYOUTS config
 *   theme   — from THEMES config
 *   photos  — string[] of dataUrls (length = layout.slots)
 *   filter  — from FILTERS config
 *   width   — number (px), optional, defaults to 400
 */
const PhotoFrame = forwardRef(function PhotoFrame(
  { layout, theme, photos = [], filter, width = 400 },
  ref
) {
  const { cols, rows, slots } = layout;
  const { frame, slots: slotStyle, header, footer, sideDecoration } = theme;

  const slotW = (width - (cols - 1) * 3) / cols; // approx slot width
  const slotH = Math.round(slotW * (3 / 4));      // 4:3 photo ratio

  return (
    <div
      ref={ref}
      style={{
        width,
        display: 'inline-flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        ...frameStyle(frame),
      }}
    >
      {/* Side film perforations (film theme only) */}
      {sideDecoration?.show && (
        <>
          <FilmSide width={sideDecoration.width} height={rows * slotH + 100} colors={sideDecoration} />
          <FilmSide width={sideDecoration.width} height={rows * slotH + 100} colors={sideDecoration} right />
        </>
      )}

      {/* Header */}
      {header?.text && (
        <div style={{
          textAlign: 'center',
          ...textStyle(header),
          position: 'relative',
          zIndex: 1,
        }}>
          {header.text}
        </div>
      )}

      {/* Photo grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, ${slotH}px)`,
        gap: slotStyle?.gap ?? '2px',
        padding: slotStyle?.padding ?? '0',
        position: 'relative',
        zIndex: 1,
      }}>
        {Array.from({ length: slots }).map((_, i) => (
          <PhotoSlot
            key={i}
            src={photos[i] || null}
            index={i}
            filter={filter}
            slotH={slotH}
          />
        ))}
      </div>

      {/* Footer */}
      {footer?.text && (
        <div style={{
          textAlign: 'center',
          ...textStyle(footer),
          position: 'relative',
          zIndex: 1,
        }}>
          {footer.text}
        </div>
      )}
    </div>
  );
});

// ── PhotoSlot ──
function PhotoSlot({ src, index, filter, slotH }) {
  return (
    <div style={{
      height: slotH,
      background: '#0A0806',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {src ? (
        <img
          src={src}
          alt={`photo-${index}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: filter?.css && filter.css !== 'none' ? filter.css : undefined,
          }}
        />
      ) : (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(240,228,208,0.12)',
          fontFamily: 'DM Mono, monospace',
          fontSize: 11,
          letterSpacing: '0.15em',
        }}>
          {index + 1}
        </div>
      )}
    </div>
  );
}

// ── Film perforations side strip ──
function FilmSide({ width, height, colors, right }) {
  const holes = Math.floor(height / 22);
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      [right ? 'right' : 'left']: 0,
      width,
      background: colors.background,
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      paddingTop: 10,
      paddingBottom: 10,
    }}>
      {Array.from({ length: holes }).map((_, i) => (
        <div key={i} style={{
          width: Math.round(width * 0.55),
          height: 9,
          borderRadius: 2,
          background: colors.perforationColor,
        }} />
      ))}
    </div>
  );
}

// ── Style helpers ──
function frameStyle(frame) {
  if (!frame) return {};
  const { background, padding, border, borderRadius, boxShadow, backgroundClip } = frame;
  return { background, padding, border, borderRadius, boxShadow, backgroundClip };
}

function textStyle(cfg) {
  if (!cfg) return {};
  const { background, color, fontFamily, fontSize, fontStyle, fontWeight,
          letterSpacing, textTransform, padding, textShadow } = cfg;
  return { background: background || 'transparent', color, fontFamily, fontSize, fontStyle,
           fontWeight, letterSpacing, textTransform, padding, textShadow };
}

export default PhotoFrame;
